import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, User, Crown } from "lucide-react";
import { useLang } from "../LanguageContext";
import { useAuth } from "../AuthContext";
import { useCurrency, SUPPORTED_CURRENCIES } from "../CurrencyContext";
import { useSettings } from "../SettingsContext";
import { IMG, resolveSiteImage } from "../brand";
import EditableText from "./EditableText";
import EditableI18n from "./EditableI18n";

const NAV = [
  { to: "/", key: "home" },
  { to: "/services", key: "services" },
  { to: "/gallery", key: "gallery" },
  { to: "/journal", key: "journal" },
  { to: "/about", key: "about" },
  { to: "/booking", key: "booking" },
  { to: "/contact", key: "contact" },
];

export default function Header() {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const { currency, setCurrency } = useCurrency();
  const { settings } = useSettings();
  const logoUrl = resolveSiteImage(settings, "logo");
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const menuRef = useRef(null);
  const currencyRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false); setMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (currencyRef.current && !currencyRef.current.contains(e.target)) setCurrencyOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // (nav labels are editable via EditableI18n below)

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-background/80 border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="pl-3 pr-4 md:pl-4 md:pr-6 lg:pl-4 lg:pr-8 xl:pl-6 xl:pr-12 py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 group shrink-0">
          <img src={logoUrl} alt="Queensway" className="h-11 w-11 object-contain rounded-full bg-foreground/95" />
          <div className="hidden sm:flex flex-col leading-none whitespace-nowrap">
            <span className="font-serif text-base tracking-wider text-foreground">QUEENSWAY</span>
            <span className="qw-overline text-[0.55rem] mt-1">Beauty &amp; Aesthetics</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
          {NAV.map((item) => (
            <NavLink
              key={item.key}
              to={item.to}
              data-testid={`nav-link-${item.key}`}
              className={({ isActive }) =>
                `qw-link-underline text-[0.68rem] xl:text-[0.72rem] tracking-[0.22em] xl:tracking-[0.28em] uppercase whitespace-nowrap transition-colors ${
                  isActive ? "text-primary" : "text-foreground/85 hover:text-primary"
                }`
              }
            >
              {item.key === "gallery"
                ? <EditableText kEn="txt_nav_gallery_en" kFr="txt_nav_gallery_fr" label="Nav: Gallery" fallback={{ en: "Gallery", fr: "Galerie" }} />
                : <EditableI18n k={`nav.${item.key}`} label={`Nav: ${item.key}`} />}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 lg:gap-4 shrink-0 whitespace-nowrap">
          <button
            data-testid="lang-toggle-btn"
            onClick={toggle}
            className="text-[0.72rem] tracking-[0.28em] uppercase text-foreground/80 hover:text-primary transition-colors"
            aria-label="Toggle language"
          >
            <span className={lang === "en" ? "text-primary" : ""}>EN</span>
            <span className="mx-2 text-foreground/40">/</span>
            <span className={lang === "fr" ? "text-primary" : ""}>FR</span>
          </button>

          {/* Currency selector */}
          <div ref={currencyRef} className="relative">
            <button
              data-testid="currency-toggle-btn"
              onClick={() => setCurrencyOpen((v) => !v)}
              className="text-[0.72rem] tracking-[0.28em] uppercase text-foreground/80 hover:text-primary transition-colors flex items-center gap-1"
              aria-label="Select currency"
              aria-expanded={currencyOpen}
            >
              <span className="text-primary">{currency}</span>
              <span className="text-foreground/40 text-[0.55rem]">▾</span>
            </button>
            {currencyOpen && (
              <div data-testid="currency-menu" className="absolute right-0 top-9 w-32 bg-background border border-border shadow-xl z-50">
                {SUPPORTED_CURRENCIES.map((c) => (
                  <button
                    key={c}
                    data-testid={`currency-option-${c}`}
                    onClick={() => { setCurrency(c); setCurrencyOpen(false); }}
                    className={`block w-full text-left px-4 py-2.5 text-[0.7rem] tracking-[0.2em] uppercase hover:bg-muted/60 transition-colors ${currency === c ? "text-primary" : "text-foreground/85"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Auth area */}
          {user ? (
            <div ref={menuRef} className="relative">
              <button
                data-testid="user-menu-btn"
                onClick={() => setMenuOpen(!menuOpen)}
                className="h-9 w-9 border border-primary/60 rounded-full flex items-center justify-center text-primary hover:bg-primary/15 transition"
                aria-label="User menu"
              >
                {user.role === "admin" ? <Crown size={14} strokeWidth={1.2} /> : <User size={14} strokeWidth={1.2} />}
              </button>
              {menuOpen && (
                <div data-testid="user-menu" className="absolute right-0 top-12 w-56 bg-background border border-border shadow-xl">
                  <div className="px-5 py-4 border-b border-border">
                    <p className="text-xs text-muted-foreground">{t.auth.welcome}</p>
                    <p className="font-serif text-base">{user.name}</p>
                  </div>
                  {user.role === "admin" ? (
                    <Link to="/admin" data-testid="menu-admin-link" className="block px-5 py-3 text-sm hover:bg-muted/50">Admin Dashboard</Link>
                  ) : (
                    <Link to="/account" data-testid="menu-account-link" className="block px-5 py-3 text-sm hover:bg-muted/50">{t.auth.account}</Link>
                  )}
                  <button data-testid="menu-logout-btn" onClick={logout} className="block w-full text-left px-5 py-3 text-sm text-destructive hover:bg-muted/50">{t.auth.logout}</button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" data-testid="header-login-link" className="hidden md:inline text-[0.72rem] tracking-[0.28em] uppercase text-foreground/85 hover:text-primary">
              <EditableI18n k="auth.login" label="Login link" />
            </Link>
          )}

          <Link to="/booking" data-testid="header-book-now-btn" className="hidden md:inline-flex qw-btn qw-btn-primary !py-2.5 !px-4 lg:!px-5 !text-[0.6rem] lg:!text-[0.65rem]">
            <EditableI18n k="nav.bookNow" label="Book Now button" />
          </Link>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="lg:hidden text-foreground" aria-label="Menu">
            {open ? <X size={22} strokeWidth={1.2} /> : <Menu size={22} strokeWidth={1.2} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-background/95 backdrop-blur-xl border-t border-border">
          <div className="flex flex-col py-6 px-8 gap-5">
            {NAV.map((item) => (
              <NavLink key={item.key} to={item.to} data-testid={`mobile-nav-${item.key}`}
                className={({ isActive }) => `text-sm tracking-[0.28em] uppercase ${isActive ? "text-primary" : "text-foreground/85"}`}>
                {item.key === "gallery" ? galleryLabel : t.nav[item.key]}
              </NavLink>
            ))}
            <Link to="/quiz" className="text-sm tracking-[0.28em] uppercase text-foreground/85">{lang === "fr" ? "Quiz" : "Quiz"}</Link>
            <Link to="/gift-cards" className="text-sm tracking-[0.28em] uppercase text-foreground/85">{lang === "fr" ? "Cartes cadeau" : "Gift Cards"}</Link>
            {!user && <Link to="/login" className="text-sm tracking-[0.28em] uppercase text-foreground/85">{t.auth.login}</Link>}
            {user && <Link to={user.role === "admin" ? "/admin" : "/account"} className="text-sm tracking-[0.28em] uppercase text-primary">{user.role === "admin" ? "Admin" : t.auth.account}</Link>}
            <Link to="/booking" data-testid="mobile-book-now" className="qw-btn qw-btn-primary mt-2 self-start">{t.nav.bookNow}</Link>
          </div>
        </div>
      )}
    </header>
  );
}
